import { useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MapPin, X } from 'lucide-react';
import { Buton, Etiket, Girdi, Rozet, toast } from '@emanetx/ui';
import { EmanetXHata } from '@emanetx/sdk';
import { dolapOlusturIstegi, type DolapOlusturIstegi } from '@emanetx/shared-types';
import { useDolapOlustur } from './api.js';

interface YeniDolapDialogOzellikleri {
  acik: boolean;
  onDegisim: (acik: boolean) => void;
  /** Hangi noktaya ekleniyor — zorunlu. */
  noktaId: string;
  noktaKod: string;
  noktaAd: string;
}

const VARSAYILAN_FORM: Omit<DolapOlusturIstegi, 'noktaId'> = {
  numara: '',
  boyut: 'ORTA',
  kiralamaSaati: 24,
  periyotUcreti: 0,
  saatlikUcret: 0,
  cezaUcreti: 0,
};

export function YeniDolapDialog({
  acik,
  onDegisim,
  noktaId,
  noktaKod,
  noktaAd,
}: YeniDolapDialogOzellikleri) {
  const olustur = useDolapOlustur();

  const form = useForm<DolapOlusturIstegi>({
    resolver: zodResolver(dolapOlusturIstegi),
    defaultValues: { ...VARSAYILAN_FORM, noktaId },
  });

  useEffect(() => {
    if (!acik) form.reset({ ...VARSAYILAN_FORM, noktaId });
  }, [acik, noktaId, form]);

  const gonder = form.handleSubmit(async (v) => {
    try {
      const yeni = await olustur.mutateAsync({ ...v, noktaId });
      toast.success(`Dolap #${yeni.numara} oluşturuldu`);
      onDegisim(false);
    } catch (err) {
      const mesaj =
        err instanceof EmanetXHata && err.kod === 'dolap_numara_zaten_var'
          ? 'Bu numarayla bir dolap zaten var.'
          : (err as Error).message;
      toast.error(mesaj);
    }
  });

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-fume-900 dark:text-fume-50 text-base font-semibold">
                Yeni Dolap
              </Dialog.Title>
              <Dialog.Description asChild>
                <div className="text-fume-500 dark:text-fume-400 mt-1.5 flex items-center gap-2 text-sm">
                  <MapPin size={14} className="text-marka-500 flex-shrink-0" />
                  <span className="truncate">{noktaAd}</span>
                  <Rozet ton="fume" className="font-mono">
                    {noktaKod}
                  </Rozet>
                </div>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Kapat"
                className="text-fume-400 hover:text-fume-700 dark:hover:text-fume-100"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={gonder} className="mt-6 space-y-4" noValidate>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Etiket htmlFor="numara">Numara</Etiket>
                <Girdi id="numara" placeholder="01" autoFocus {...form.register('numara')} />
                {form.formState.errors.numara && (
                  <p className="text-xs text-red-600">{form.formState.errors.numara.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="boyut">Boyut</Etiket>
                <select
                  id="boyut"
                  className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
                  {...form.register('boyut')}
                >
                  <option value="KUCUK">Küçük (S)</option>
                  <option value="ORTA">Orta (M)</option>
                  <option value="BUYUK">Büyük (L)</option>
                  <option value="EKSTRA">Ekstra (XL)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Etiket htmlFor="kiralamaSaati">Periyot (saat)</Etiket>
                <Girdi
                  id="kiralamaSaati"
                  type="number"
                  min={1}
                  {...form.register('kiralamaSaati', { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="periyotUcreti">Periyot ücreti</Etiket>
                <Girdi
                  id="periyotUcreti"
                  type="number"
                  step="0.01"
                  {...form.register('periyotUcreti', { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="saatlikUcret">Saatlik aşım</Etiket>
                <Girdi
                  id="saatlikUcret"
                  type="number"
                  step="0.01"
                  {...form.register('saatlikUcret', { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="cezaUcreti">Ceza</Etiket>
                <Girdi
                  id="cezaUcreti"
                  type="number"
                  step="0.01"
                  {...form.register('cezaUcreti', { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" disabled={olustur.isPending}>
                  Vazgeç
                </Buton>
              </Dialog.Close>
              <Buton type="submit" disabled={olustur.isPending}>
                {olustur.isPending ? 'Oluşturuluyor...' : 'Oluştur'}
              </Buton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
